import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type MouseEvent,
  type ReactNode,
  type SyntheticEvent,
} from "react";
import styled, { css } from "styled-components";

import { mediaViewportFitCss, mediaViewportFitWidthCss } from "../foundations/mediaViewportFit";
import { Spinner } from "../Spinner";
import { ZoomPanWrapper } from "../ZoomPan";
import { resolveVideoFrameAlignment } from "./frameAlignment";
import { useVideoTimeline } from "./playbackTransport";
import { VideoCaption } from "./VideoCaption";
import { VideoFrame } from "./VideoFrame";
import { pruneVideoChannelRecord } from "./pruneVideoChannelState";
import { BUFFERING_INDICATOR_DELAY_MS, useDelayedFlag } from "./useDelayedFlag";
import {
  useSyncedVideoTimeline,
  type SyncedVideoTimelineApi,
  type UseSyncedVideoTimelineOptions,
} from "./useSyncedVideoTimeline";
import type {
  VideoAspectRatioMode,
  VideoChannelEndBehavior,
  VideoChannelSource,
  VideoFrameAlign,
  VideoObjectFit,
  VideoSourceSizeMode,
} from "./types";

const DEFAULT_ASPECT_RATIO = 16 / 9;

export interface SyncedVideoGridLabels {
  resetView: string;
}

const DEFAULT_LABELS: SyncedVideoGridLabels = {
  resetView: "Reset view",
};

const Grid = styled.div`
  display: grid;
  width: 100%;
  min-width: 0;
  gap: 0.75rem;
  grid-template-columns: repeat(auto-fit, minmax(min(18rem, 100%), 1fr));
`;

const Tile = styled.div`
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 0.35rem;

  &[hidden] {
    display: none;
  }
`;

interface FrameLayoutStyleProps {
  $aspectRatio: number;
  $frameAlign: VideoFrameAlign;
  $maxHeight?: string;
  $width?: string;
}

const AlignedVideoCaption = styled(VideoCaption)<FrameLayoutStyleProps>`
  ${({ $aspectRatio, $frameAlign, $maxHeight, $width }) =>
    $maxHeight === undefined
      ? undefined
      : css`
          ${mediaViewportFitWidthCss({
            aspectRatio: $aspectRatio,
            maxHeight: $maxHeight,
            intrinsicWidth: $width,
          })}
          align-self: ${resolveVideoFrameAlignment($frameAlign)};
        `}
`;

const FrameSlot = styled.div<FrameLayoutStyleProps>`
  position: relative;
  /* $width is present only for sourceSizeMode="intrinsic" after metadata loads. */
  ${({ $aspectRatio, $maxHeight, $width }) =>
    $maxHeight !== undefined
      ? mediaViewportFitCss({
          aspectRatio: $aspectRatio,
          maxHeight: $maxHeight,
          intrinsicWidth: $width,
        })
      : css`
          width: ${$width ?? "100%"};
          max-width: 100%;
          max-height: none;
          aspect-ratio: auto;
        `}
  min-width: 0;
  align-self: ${({ $frameAlign }) => resolveVideoFrameAlignment($frameAlign)};
`;

const EmptyState = styled.div`
  width: 100%;
  padding: 1rem;
  color: ${({ theme }) => theme.color.text.subtle};
  background-color: ${({ theme }) => theme.color.bg.surfaceSunken};
  border: 1px solid ${({ theme }) => theme.color.border.subtle};
  border-radius: ${({ theme }) => theme.shape.radius.surface};
`;

const BlankFrame = styled.div`
  width: 100%;
  height: 100%;
  background-color: ${({ theme }) => theme.color.scrim.default};
`;

// 버퍼 대기 중 아직 받지 못한 채널의 표시 — 어느 카메라가 느린지 보인다. zoom/pan transform 밖(슬롯
// 레벨)에 두어 확대해도 프레임 가운데에 머문다. 영상 픽셀 위라 불투명 면을 깐다
// (PIT-transparent-overlay-contrast). 상태 문구는 재생 바가 말하므로 장식용이다.
const BufferingLayer = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
`;

const BufferingChip = styled.span`
  display: inline-flex;
  padding: 0.4rem;
  border-radius: ${({ theme }) => theme.shape.radius.control};
  background-color: ${({ theme }) => theme.color.bg.surfaceOverlay};
  color: ${({ theme }) => theme.color.text.default};
`;

/** 내부에서 생성하는 타임라인에 전달되는 옵션(`timeline`을 주면 무시된다). */
interface SelfDrivenOptions {
  durationOverrideSeconds?: UseSyncedVideoTimelineOptions["durationOverrideSeconds"];
  driftToleranceSeconds?: UseSyncedVideoTimelineOptions["driftToleranceSeconds"];
  endEpsilonSeconds?: UseSyncedVideoTimelineOptions["endEpsilonSeconds"];
  emitIntervalMs?: UseSyncedVideoTimelineOptions["emitIntervalMs"];
  channelEndBehavior?: VideoChannelEndBehavior;
  onTimelineEnded?: UseSyncedVideoTimelineOptions["onTimelineEnded"];
  waitForBuffer?: UseSyncedVideoTimelineOptions["waitForBuffer"];
  bufferAheadSeconds?: UseSyncedVideoTimelineOptions["bufferAheadSeconds"];
}

/** 두 모드가 공유하는 표현(presentational) 옵션. */
interface GridViewOptions {
  emptyMessage?: ReactNode;
  showLabels?: boolean;
  labels?: Partial<SyncedVideoGridLabels>;
  defaultAspectRatio?: number;
  aspectRatioMode?: VideoAspectRatioMode;
  sourceSizeMode?: VideoSourceSizeMode;
  frameAlign?: VideoFrameAlign;
  /**
   * 채널별 media frame 높이 cap(CSS length). fill mode는 child intrinsic size와 무관하게
   * resolved aspect ratio target까지 frame slot 폭을 확대·축소하고, intrinsic mode는 source
   * width를 추가 상한으로 둔다. caption은 transform frame 밖에 두되 fitted slot과 같은 폭·정렬을
   * 쓴다. fullscreen에서는 caption/chrome 예산을 빼서 전달하고, 좁아진 slot을 가운데 둘 때는
   * `frameAlign="center"`를 함께 준다.
   */
  maxHeight?: string;
  objectFit?: VideoObjectFit;
  preload?: "none" | "metadata" | "auto";
  zoomable?: boolean;
  /** zoomable 타일의 줌 하한(`ZoomPanWrapper.minZoom` passthrough). */
  minZoom?: number;
  /** zoomable 타일의 줌 상한(`ZoomPanWrapper.maxZoom` passthrough). */
  maxZoom?: number;
  /** zoomable 타일의 휠 노치당 줌 스텝(`ZoomPanWrapper.zoomStep` passthrough). */
  zoomStep?: number;
  /**
   * zoomable 타일의 viewport 크기가 바뀔 때 px pan을 축별로 rebase해 상대 영상 지점을
   * 보존한다. inline↔overlay stable presentation처럼 같은 subtree를 다른 크기 host로 옮길 때
   * 켠다. 기본 false로 기존 고정 px transform 동작을 유지한다.
   */
  preserveRelativeViewOnResize?: boolean;
  /**
   * 표시할 channel ID subset. 생략하면 전부 표시한다. 제외된 타일은 `hidden`이지만 mount와
   * timeline registration은 유지되므로 stable presentation 전환 중 decoder/ZoomPan state가
   * 사라지지 않는다. channel resource를 실제로 제거하려면 `channels`에서 빼야 한다.
   */
  visibleChannelIds?: readonly string[];
  /**
   * 채널별 프레임 우상단 액션 — 전체화면은 `MediaFullscreenButton` 프리셋을 채널 클로저로
   * 조립하는 것이 표준(확대 뷰·오버레이는 consumer 소유). `zoomable`이면 타일이
   * `ZoomPanWrapper.actions`로 승격해 transform 밖(viewport 레벨)에 앉히고 reset 컨트롤과
   * 한 row로 정렬된다.
   */
  renderFrameActions?: (channel: VideoChannelSource) => ReactNode;
  onChannelReady?: (
    channel: VideoChannelSource,
    event: SyntheticEvent<HTMLVideoElement, Event>,
  ) => void;
  onChannelError?: (
    channel: VideoChannelSource,
    event: SyntheticEvent<HTMLVideoElement, Event>,
  ) => void;
  onChannelClick?: (channel: VideoChannelSource, event: MouseEvent<HTMLVideoElement>) => void;
}

export interface SyncedVideoGridProps extends SelfDrivenOptions, GridViewOptions {
  channels: readonly VideoChannelSource[];
  /**
   * 타임라인을 공유한다(예: 같은 엔진으로 `VideoPlaybackBar`까지 구동). 생략하면 그리드가
   * `SelfDrivenOptions`로 자체 타임라인을 만들어 소유한다. 타임라인에 넘긴 것과 동일한
   * `channels`를 넘겨라. 마운트마다 한 모드를 정해 고정하라.
   */
  timeline?: SyncedVideoTimelineApi;
}

function resolveAspectRatio(
  channel: VideoChannelSource,
  mode: VideoAspectRatioMode,
  defaultAspectRatio: number,
  sourceRatios: Record<string, number>,
) {
  if (mode === "channel") {
    return channel.aspectRatio ?? defaultAspectRatio;
  }

  if (mode === "source") {
    return sourceRatios[channel.id] ?? channel.aspectRatio ?? defaultAspectRatio;
  }

  return defaultAspectRatio;
}

interface SyncedVideoTileProps {
  channel: VideoChannelSource;
  timeline: SyncedVideoTimelineApi;
  showLabel: boolean;
  zoomable: boolean;
  minZoom?: number;
  maxZoom?: number;
  zoomStep?: number;
  preserveRelativeViewOnResize: boolean;
  frameAlign: VideoFrameAlign;
  aspectRatio: number;
  maxHeight?: string;
  width?: string;
  objectFit: VideoObjectFit;
  resetLabel: string;
  preload: "none" | "metadata" | "auto";
  visible: boolean;
  actions?: ReactNode;
  onMetadata: (channel: VideoChannelSource, event: SyntheticEvent<HTMLVideoElement, Event>) => void;
  onReady?: (channel: VideoChannelSource, event: SyntheticEvent<HTMLVideoElement, Event>) => void;
  onError?: (channel: VideoChannelSource, event: SyntheticEvent<HTMLVideoElement, Event>) => void;
  onClick?: (channel: VideoChannelSource, event: MouseEvent<HTMLVideoElement>) => void;
}

// 채널 하나를 그리는 타일. `<video>`를 타임라인에 등록하고, blank·버퍼 대기 여부만 구독한다.
const SyncedVideoTile = memo(function SyncedVideoTile({
  channel,
  timeline,
  showLabel,
  zoomable,
  minZoom,
  maxZoom,
  zoomStep,
  preserveRelativeViewOnResize,
  frameAlign,
  aspectRatio,
  maxHeight,
  width,
  objectFit,
  resetLabel,
  preload,
  visible,
  actions,
  onMetadata,
  onReady,
  onError,
  onClick,
}: SyncedVideoTileProps) {
  // 프레임의 `<video>` 엘리먼트(VideoFrame이 ref로 그대로 forward)를 타임라인에 등록한다.
  // 예전처럼 VideoSurface의 controlled API를 우회해 엘리먼트만 꺼내 쓰지 않는다.
  const registerFrame = useMemo(() => timeline.registerVideo(channel.id), [channel.id, timeline]);

  // 이 채널의 out-of-time 불리언만 구독한다: 타일은 그 채널이 자기 끝을 지날 때만 리렌더되고,
  // 매 currentTime 프레임마다 리렌더되지 않는다.
  const outOfTime = useVideoTimeline(timeline, () => timeline.isChannelOutOfTime(channel.id));
  const buffering = useVideoTimeline(
    timeline,
    () => timeline.isChannelBuffering?.(channel.id) ?? false,
  );
  const bufferingVisible = useDelayedFlag(buffering && !outOfTime, BUFFERING_INDICATOR_DELAY_MS);

  const frame = (
    <VideoFrame
      ref={registerFrame}
      src={channel.src}
      poster={channel.poster}
      crossOrigin={channel.crossOrigin}
      muted
      preload={preload}
      aspectRatio={aspectRatio}
      maxHeight={maxHeight}
      objectFit={objectFit}
      overlay={outOfTime ? <BlankFrame aria-hidden="true" /> : undefined}
      actions={zoomable ? undefined : actions}
      aria-label={typeof channel.label === "string" ? channel.label : undefined}
      onLoadedMetadata={(event) => onMetadata(channel, event)}
      onLoadedData={(event) => onReady?.(channel, event)}
      onError={(event) => onError?.(channel, event)}
      onClick={(event) => onClick?.(channel, event)}
    />
  );

  return (
    <Tile hidden={!visible}>
      <AlignedVideoCaption
        show={showLabel}
        label={channel.label}
        subLabel={channel.subLabel}
        $aspectRatio={aspectRatio}
        $frameAlign={frameAlign}
        $maxHeight={maxHeight}
        $width={width}
      />
      <FrameSlot
        $aspectRatio={aspectRatio}
        $frameAlign={frameAlign}
        $maxHeight={maxHeight}
        $width={width}
      >
        {zoomable ? (
          // 액션은 transform 밖(viewport 레벨)에 앉아야 zoom/pan을 따라가지 않는다.
          <ZoomPanWrapper
            bordered
            showResetButton
            resetLabel={resetLabel}
            minZoom={minZoom}
            maxZoom={maxZoom}
            zoomStep={zoomStep}
            preserveRelativeViewOnResize={preserveRelativeViewOnResize}
            actions={actions}
          >
            {frame}
          </ZoomPanWrapper>
        ) : (
          frame
        )}
        {bufferingVisible ? (
          <BufferingLayer aria-hidden="true" data-channel-buffering="">
            <BufferingChip>
              <Spinner size="md" />
            </BufferingChip>
          </BufferingLayer>
        ) : null}
      </FrameSlot>
    </Tile>
  );
});

interface SyncedVideoGridViewProps extends GridViewOptions {
  channels: readonly VideoChannelSource[];
  timeline: SyncedVideoTimelineApi;
}

// 타임라인을 받아 타일 그리드를 그리는 표현(presentational) 컴포넌트.
function SyncedVideoGridView({
  channels,
  timeline,
  emptyMessage = "No videos available.",
  showLabels = true,
  labels,
  defaultAspectRatio = DEFAULT_ASPECT_RATIO,
  aspectRatioMode = "fixed",
  sourceSizeMode = "fill",
  frameAlign = "start",
  maxHeight,
  objectFit = "contain",
  preload = "metadata",
  zoomable = false,
  minZoom,
  maxZoom,
  zoomStep,
  preserveRelativeViewOnResize = false,
  visibleChannelIds,
  renderFrameActions,
  onChannelReady,
  onChannelError,
  onChannelClick,
}: SyncedVideoGridViewProps) {
  const [sourceRatios, setSourceRatios] = useState<Record<string, number>>({});
  const [sourceWidths, setSourceWidths] = useState<Record<string, number>>({});
  const mergedLabels = useMemo(() => ({ ...DEFAULT_LABELS, ...labels }), [labels]);
  const activeChannelIds = useMemo(
    () => new Set(channels.map((channel) => channel.id)),
    [channels],
  );
  const visibleChannelIdSet = useMemo(
    () => (visibleChannelIds ? new Set(visibleChannelIds) : null),
    [visibleChannelIds],
  );

  useEffect(() => {
    setSourceRatios((prev) => pruneVideoChannelRecord(prev, activeChannelIds));
    setSourceWidths((prev) => pruneVideoChannelRecord(prev, activeChannelIds));
  }, [activeChannelIds]);

  const captureSourceMetadata = useCallback(
    (channel: VideoChannelSource, event: SyntheticEvent<HTMLVideoElement, Event>) => {
      const { videoWidth, videoHeight } = event.currentTarget;
      if (videoWidth > 0 && videoHeight > 0) {
        const ratio = videoWidth / videoHeight;
        setSourceRatios((prev) =>
          prev[channel.id] === ratio ? prev : { ...prev, [channel.id]: ratio },
        );
        if (sourceSizeMode === "intrinsic") {
          setSourceWidths((prev) =>
            prev[channel.id] === videoWidth ? prev : { ...prev, [channel.id]: videoWidth },
          );
        }
      }
    },
    [sourceSizeMode],
  );

  if (channels.length === 0) {
    return <EmptyState role="status">{emptyMessage}</EmptyState>;
  }

  return (
    <Grid>
      {/* TBD(Gigone): 구현 예정 — 대량 채널 fanout 최적화(virtualization/lazy-mount/상한).
          현재는 모든 채널을 동시에 mount·디코드한다(기기 bound). */}
      {channels.map((channel) => {
        const aspectRatio = resolveAspectRatio(
          channel,
          aspectRatioMode,
          defaultAspectRatio,
          sourceRatios,
        );
        const width =
          sourceSizeMode === "intrinsic" && sourceWidths[channel.id] != null
            ? `${sourceWidths[channel.id]}px`
            : undefined;

        return (
          <SyncedVideoTile
            key={channel.id}
            channel={channel}
            timeline={timeline}
            showLabel={showLabels}
            zoomable={zoomable}
            minZoom={minZoom}
            maxZoom={maxZoom}
            zoomStep={zoomStep}
            preserveRelativeViewOnResize={preserveRelativeViewOnResize}
            frameAlign={frameAlign}
            aspectRatio={aspectRatio}
            maxHeight={maxHeight}
            width={width}
            objectFit={objectFit}
            resetLabel={mergedLabels.resetView}
            preload={preload}
            visible={visibleChannelIdSet?.has(channel.id) ?? true}
            actions={renderFrameActions?.(channel)}
            onMetadata={captureSourceMetadata}
            onReady={onChannelReady}
            onError={onChannelError}
            onClick={onChannelClick}
          />
        );
      })}
    </Grid>
  );
}

// `timeline`을 안 받은 경우: 그리드가 자체 타임라인을 만들어 소유한다(편의 케이스).
function SelfDrivenSyncedVideoGrid({
  channels,
  durationOverrideSeconds,
  driftToleranceSeconds,
  endEpsilonSeconds,
  emitIntervalMs,
  channelEndBehavior = "blank",
  onTimelineEnded,
  waitForBuffer,
  bufferAheadSeconds,
  ...viewProps
}: Omit<SyncedVideoGridProps, "timeline">) {
  const timeline = useSyncedVideoTimeline({
    channels,
    durationOverrideSeconds,
    driftToleranceSeconds,
    endEpsilonSeconds,
    emitIntervalMs,
    channelEndBehavior,
    onTimelineEnded,
    waitForBuffer,
    bufferAheadSeconds,
  });

  return <SyncedVideoGridView {...viewProps} channels={channels} timeline={timeline} />;
}

// 디스패치: 외부 `timeline`을 받으면 공유 모드, 없으면 자체 생성 모드로 분기한다.
export function SyncedVideoGrid({
  timeline,
  durationOverrideSeconds,
  driftToleranceSeconds,
  endEpsilonSeconds,
  emitIntervalMs,
  channelEndBehavior,
  onTimelineEnded,
  waitForBuffer,
  bufferAheadSeconds,
  ...viewProps
}: SyncedVideoGridProps) {
  if (timeline) {
    return <SyncedVideoGridView {...viewProps} timeline={timeline} />;
  }
  return (
    <SelfDrivenSyncedVideoGrid
      {...viewProps}
      durationOverrideSeconds={durationOverrideSeconds}
      driftToleranceSeconds={driftToleranceSeconds}
      endEpsilonSeconds={endEpsilonSeconds}
      emitIntervalMs={emitIntervalMs}
      channelEndBehavior={channelEndBehavior}
      onTimelineEnded={onTimelineEnded}
      waitForBuffer={waitForBuffer}
      bufferAheadSeconds={bufferAheadSeconds}
    />
  );
}

SyncedVideoGrid.displayName = "SyncedVideoGrid";
